/**
 * REPSit.ai Intelligent Assistant registry — single source of truth for the
 * ten IAs (names, titles, card copy, avatars, active flag, rollout order).
 *
 * Sources (Dropbox › HUGO): `IA-names-and-descriptions.docx` (names + long
 * titles), `Entry-page mockup.png` (short titles), `Client-agent-survey-results.pdf`
 * ("what it does", condensed), `media-graphics/IA Avatars/*.png` (artwork —
 * optimized copies live in `public/avatars/`). Order = client survey rank.
 *
 * Usage (P6‑02 part 2): the entry page, the "Coming to REPSit.ai" cards, the
 * sidebars and the DREW badge import from here instead of hard‑coding names.
 *
 *   import { ASSISTANTS, getAssistant, ACTIVE_ASSISTANTS } from '@/assistants/assistants';
 *
 * Avatar rule (plan §1b): the name plate is part of the artwork. At ≥160 px
 * show the image alone plus `description`; at ≤64 px (badge, sidebar header,
 * small cards) pair `avatarSmall` with the text `name`.
 *
 * Nothing imports this file yet — it is the registry for Beta‑1 phase P6.
 */

const AVATAR_BASE = '/avatars';

export const ASSISTANTS = Object.freeze([
  {
    id: 'drew',
    name: 'DREW',
    order: 1,
    active: true,
    longTitle: 'REF Drift & Accumulation Early-Warning Agent',
    shortTitle: 'Drift & Accumulation Early Warning System',
    description:
      'Live assessment of Organizational Entropy — drift and the accumulation of Traps, Roadblocks and Landmines — with REPS scores, threshold warnings and Reverse/Eliminate/Fortify direction before conditions become events.',
    avatar: `${AVATAR_BASE}/drew.webp`,
    avatarSmall: `${AVATAR_BASE}/drew-128.webp`,
    accent: '#EF9F27', // Signal Amber — the orbit dot in DREW's artwork
    route: '/drew',
  },
  {
    id: 'hugo',
    name: 'HUGO',
    order: 2,
    active: false,
    longTitle: 'Human Performance Barometer & REPS Optimizer',
    shortTitle: 'Human Performance Barometer',
    description:
      'Generates a predictive indication of whether human performance is heading in a positive or negative direction and recommends REPS balance adjustments before metrics deteriorate.',
    avatar: `${AVATAR_BASE}/hugo.webp`,
    avatarSmall: `${AVATAR_BASE}/hugo-128.webp`,
    accent: null,
    route: '/hugo',
  },
  {
    id: 'bria',
    name: 'BRIA',
    order: 3,
    active: false,
    longTitle: 'Job Briefing & After-Action Review Generator',
    shortTitle: 'Job Briefings & After-Action Reviews',
    description:
      'Prepares job briefings based on task complexity, current conditions, and lessons learned; prompts the After-Action Review and captures what was learned.',
    avatar: `${AVATAR_BASE}/bria.webp`,
    avatarSmall: `${AVATAR_BASE}/bria-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'tess',
    name: 'TESS',
    order: 4,
    active: false,
    longTitle: 'Error Trap Identifier & Power Tool Fortifier',
    shortTitle: 'Setup Identifier & Tool Fortifier',
    description:
      'Scans procedures, processes and work environments for the 11 most common error traps and delivers matching Error Elimination Tools.',
    avatar: `${AVATAR_BASE}/tess.webp`,
    avatarSmall: `${AVATAR_BASE}/tess-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'linc',
    name: 'LINC',
    order: 5,
    active: false,
    longTitle: 'Frontline Supervisor Mastery Accelerator',
    shortTitle: 'FLS Mastery Accelerator',
    description:
      'Daily, role-specific coaching for frontline supervisors — the Linchpins — on drift and trap recognition, proactive safety conversations, and people leadership.',
    avatar: `${AVATAR_BASE}/linc.webp`,
    avatarSmall: `${AVATAR_BASE}/linc-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'roy',
    name: 'ROY',
    order: 6,
    active: false,
    longTitle: 'HOP ROI Forecaster & Continuous Tracker',
    shortTitle: 'ROI Forecaster & Tracker',
    description:
      'Projects and tracks the financial, safety, and cultural ROI of your HOP implementation in the business language executives need.',
    avatar: `${AVATAR_BASE}/roy.webp`,
    avatarSmall: `${AVATAR_BASE}/roy-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'quinn',
    name: 'QUINN',
    order: 7,
    active: false,
    longTitle: 'HOP Quick-Start Implementation/Upgrade Agent',
    shortTitle: 'HOP Implementation & Integration',
    description:
      'A rapid, jargon-free start or upgrade of your HOP journey — proposes and optimizes an implementation plan around your constraints and opportunities.',
    avatar: `${AVATAR_BASE}/quinn.webp`,
    avatarSmall: `${AVATAR_BASE}/quinn-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'penn',
    name: 'PENN',
    order: 8,
    active: false,
    longTitle: 'Learning Teams (LT) Scribe, Coach & Mentor',
    shortTitle: 'Learning Team Scribe, Coach & Mentor',
    description:
      'Captures Learning Team sessions, gives objective analysis and feedback to facilitators and members, and tracks next steps to completion.',
    avatar: `${AVATAR_BASE}/penn.webp`,
    avatarSmall: `${AVATAR_BASE}/penn-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'vera',
    name: 'VERA',
    order: 9,
    active: false,
    longTitle: 'Cultural Virtues Diagnostic Agent',
    shortTitle: 'Cultural Virtues Accelerator',
    description:
      'Assesses the 26 Cultural Virtues at individual and team levels and delivers prioritized, principle-based action plans.',
    avatar: `${AVATAR_BASE}/vera.webp`,
    avatarSmall: `${AVATAR_BASE}/vera-128.webp`,
    accent: null,
    route: null,
  },
  {
    id: 'cody',
    name: 'CODY',
    order: 10,
    active: false,
    longTitle: 'Code of Honor Builder & Living Sustainment Agent',
    shortTitle: 'Code of Honor: Build & Sustain',
    description:
      "Assists Code of Honor facilitation and then provides principle-centered nudges and celebration triggers to keep each team's Code 'alive' and front-of-mind.",
    avatar: `${AVATAR_BASE}/cody.webp`,
    avatarSmall: `${AVATAR_BASE}/cody-128.webp`,
    accent: null,
    route: null,
  },
]);

export const ACTIVE_ASSISTANTS = ASSISTANTS.filter((a) => a.active);
export const COMING_ASSISTANTS = ASSISTANTS.filter((a) => !a.active);

const BY_ID = new Map(ASSISTANTS.map((a) => [a.id, a]));

/** Look up an assistant by id (case-insensitive: 'drew' | 'DREW'). */
export function getAssistant(id) {
  return BY_ID.get(String(id || '').toLowerCase()) || null;
}

export default ASSISTANTS;
